import {
  Accordion,
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Group,
  List,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import {
  LuArrowRight,
  LuCheck,
  LuClipboardList,
  LuCreditCard,
  LuFileText,
  LuFlaskConical,
  LuLanguages,
  LuMap,
  LuReceipt,
  LuSparkles,
  LuTrendingUp,
  LuUsers,
} from 'react-icons/lu'
import { ThemeToggle } from '@/frontend/components/ThemeToggle'
import classes from './index.module.css'

export const Route = createFileRoute('/')({
  component: LandingPage,
})

function LandingPage() {
  return (
    <Box className={classes.page}>
      <Nav />
      <Hero />
      <Stats />
      <ClientStrip />
      <HowItWorks />
      <FeatureShowcase />
      <IndonesiaAdvantage />
      <Testimonials />
      <Pricing />
      <Faq />
      <FinalCta />
      <Footer />
    </Box>
  )
}

// ─── NAV ─────────────────────────────────────────────

function Nav() {
  return (
    <Box component="nav" className={classes.nav}>
      <Container size="xl">
        <Group justify="space-between" py="sm">
          <Group gap="xs">
            <BrandIcon />
            <Text className={classes.brandWord}>JP MARINE</Text>
          </Group>
          <Group gap="lg" visibleFrom="sm">
            <Link to="/features" className={classes.navLink}>
              Fitur
            </Link>
            <Link to="/pricing" className={classes.navLink}>
              Harga
            </Link>
            <Link to="/contact" className={classes.navLink}>
              Kontak
            </Link>
          </Group>
          <Group gap="xs" wrap="nowrap">
            <ThemeToggle size="sm" />
            {/* Renamed from "Sign in" to make the B2B intent explicit:
                this entry point is for staff who already have an invite
                from their marina admin. Random visitors curious about the
                product are funneled to the demo CTA below. */}
            <Button component={Link} to="/login" variant="subtle" size="sm">
              Login Staff
            </Button>
            {/* Primary CTA in nav = same DemoCTA as hero. One button, one
                meaning: "click and you're inside, no form". */}
            <DemoCTA size="sm" />
          </Group>
        </Group>
      </Container>
    </Box>
  )
}

function BrandIcon() {
  return (
    <Box className={classes.brandIcon}>
      {Array.from({ length: 9 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: 9 fixed brand-icon cells; index is identity
        <Box key={i} className={classes.brandIconCell} style={{ opacity: 0.7 + (i % 4) * 0.1 }} />
      ))}
    </Box>
  )
}

// ─── HERO ────────────────────────────────────────────

function Hero() {
  return (
    <Box className={classes.hero}>
      <Container size="xl" pt={80} pb={60}>
        <Grid gutter={{ base: 'xl', md: 60 }} align="center">
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Badge color="laut" variant="light" mb="lg" size="lg">
              PERANGKAT LUNAK MANAJEMEN MARINA — INDONESIA FIRST
            </Badge>
            <Title order={1} fz={{ base: 40, sm: 56 }} lh={1.1} mb="md">
              Marina Management,
              <br />
              <Text component="span" inherit className={classes.heroAccent}>
                Jadi Mudah
              </Text>
              .
            </Title>
            <Text className={classes.heroSubtext} mb="xl" maw={520}>
              Kelola berth, booking, tagihan, dan pelanggan marina Anda dalam satu software. Native Bahasa Indonesia.
              Siap QRIS. Mulai gratis 30 hari.
            </Text>
            <Group gap="sm" mb="sm">
              <DemoCTA size="lg" />
              <Button component={Link} to="/features" variant="default" size="lg" visibleFrom="sm">
                Lihat Fitur
              </Button>
            </Group>
            {/* Sub-CTA: explicit "no form, no signup" reassurance directly under
                the primary button — addresses "demo terlalu ribet" perception
                head-on so visitors aren't afraid to click. */}
            <Text className={classes.heroSubMicrocopy} mb="lg">
              ⚡ Login otomatis · 0 form · Akses penuh sebagai Manager
            </Text>
            <Group gap="lg">
              {['Tanpa kartu kredit', 'Setup 1 jam', 'Data import gratis'].map((t) => (
                <Group key={t} gap={6} wrap="nowrap">
                  <LuCheck size={16} className={classes.heroCheckIcon} />
                  <Text className={classes.heroCheck}>{t}</Text>
                </Group>
              ))}
            </Group>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 5 }}>
            <Box style={{ position: 'relative' }}>
              <Box className={classes.mockup}>
                <Box className={classes.mockupHeader}>
                  <Group gap={6}>
                    <Box w={10} h={10} bg="red.4" style={{ borderRadius: '50%' }} />
                    <Box w={10} h={10} bg="yellow.4" style={{ borderRadius: '50%' }} />
                    <Box w={10} h={10} bg="green.4" style={{ borderRadius: '50%' }} />
                    <Text className={classes.mockupAddress}>admin.open-marina.id/dashboard</Text>
                  </Group>
                </Box>
                <Stack p="md" gap="sm">
                  <Group gap="xs">
                    <Badge color="laut" variant="light" size="sm">
                      DASHBOARD
                    </Badge>
                    <Text fw={600} size="sm">
                      Benoa Marina Bali
                    </Text>
                  </Group>
                  <SimpleGrid cols={3} spacing="xs">
                    <MiniStat label="Tersedia" value="45" />
                    <MiniStat label="Berlabuh" value="8" />
                    <MiniStat label="Dipesan" value="3" />
                  </SimpleGrid>
                  <Box className={classes.recentBookingCard}>
                    <Text className={classes.recentBookingLabel}>Booking Hari Ini</Text>
                    <Stack gap={6} mt={6}>
                      <Group justify="space-between">
                        <Text size="xs">Spirit 50 · Ulf E.</Text>
                        <Badge color="laut" size="xs">
                          BERLABUH
                        </Badge>
                      </Group>
                      <Group justify="space-between">
                        <Text size="xs">Marlin 43 · Wayan S.</Text>
                        <Badge color="orange" size="xs">
                          PENDING
                        </Badge>
                      </Group>
                      <Group justify="space-between">
                        <Text size="xs">Leopard 48 · Kiran R.</Text>
                        <Badge color="teal" size="xs">
                          TERKONFIRMASI
                        </Badge>
                      </Group>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
              <Box
                className={classes.floatingCard}
                style={{ position: 'absolute', top: -20, right: -16, maxWidth: 200 }}
              >
                <Group gap="xs" wrap="nowrap">
                  <ThemeIcon variant="light" color="karang" size="md" radius="xl">
                    <LuReceipt size={14} />
                  </ThemeIcon>
                  <div>
                    <Text size="sm" fw={600}>
                      Rp 3.4jt paid
                    </Text>
                    <Text size="xs" c="dimmed">
                      INV-2026-0008
                    </Text>
                  </div>
                </Group>
              </Box>
              <Box
                className={classes.floatingCard}
                style={{ position: 'absolute', bottom: -10, left: -16, maxWidth: 220 }}
              >
                <Group gap="xs" wrap="nowrap">
                  <ThemeIcon variant="light" color="laut" size="md" radius="xl">
                    <LuSparkles size={14} />
                  </ThemeIcon>
                  <div>
                    <Text size="sm" fw={600}>
                      Berth C-7 ditetapkan
                    </Text>
                    <Text size="xs" c="dimmed">
                      BK-2026-0031 · Walk-in
                    </Text>
                  </div>
                </Group>
              </Box>
            </Box>
          </Grid.Col>
        </Grid>
      </Container>
    </Box>
  )
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <Box className={classes.miniStat}>
      <Text className={classes.miniStatValue}>{value}</Text>
      <Text className={classes.miniStatLabel}>{label}</Text>
    </Box>
  )
}

// ─── STATS ───────────────────────────────────────────

function Stats() {
  const items = [
    { value: '50+', label: 'Marina dikelola' },
    { value: '10.000+', label: 'Booking diproses' },
    { value: 'Rp 50M+', label: 'Transaksi per bulan' },
    { value: '99.9%', label: 'Uptime SLA' },
  ]
  return (
    <Container size="xl" py="xl">
      <SimpleGrid cols={{ base: 2, md: 4 }}>
        {items.map((i) => (
          <Stack key={i.label} gap={4} align="center" py="md">
            <Text className={classes.statValue} fz={{ base: 28, sm: 40 }}>
              {i.value}
            </Text>
            <Text className={classes.statLabel}>{i.label}</Text>
          </Stack>
        ))}
      </SimpleGrid>
    </Container>
  )
}

// ─── FEATURE SHOWCASE ────────────────────────────────

function FeatureShowcase() {
  const features = [
    {
      category: 'RESERVASI',
      icon: LuClipboardList,
      title: 'Booking Multi-Channel',
      desc: 'Walk-in, telepon, email, agen, radio — semua masuk ke satu dashboard dengan attribution channel yang jelas.',
      bullets: [
        'Status Pending/Waitlisted/Confirmed/Declined',
        'Channel attribution per booking',
        'Auto-notifikasi ke pelanggan',
      ],
    },
    {
      category: 'OCCUPANCY',
      icon: LuMap,
      title: 'Peta Berth Real-Time',
      desc: 'Interactive map dengan status tiap berth real-time. Drag booking untuk assign/reassign, overlap detection otomatis.',
      bullets: ['Pan & zoom native', 'Click berth untuk detail booking', 'Admin edit layout drag-drop'],
    },
    {
      category: 'BILLING',
      icon: LuCreditCard,
      title: 'Invoice PPN + QRIS + Pro Forma',
      desc: 'Generate quote → invoice → proforma → paid. Format tagihan sesuai regulasi Indonesia, integrasi Midtrans/Xendit.',
      bullets: ['PPN 11% auto-calc', 'QRIS payment', 'Multi-currency ready'],
    },
    {
      category: 'CONTRACTS',
      icon: LuFileText,
      title: 'Kontrak Jangka Panjang',
      desc: 'Wizard 5-langkah: customer, detail, items, documents + questions, review. Digital signature ready.',
      bullets: ['6 status tracking', 'Recurring invoice otomatis', 'Expiry reminder'],
    },
    {
      category: 'ACCOUNTS',
      icon: LuUsers,
      title: 'CRM Pelanggan & Kapal',
      desc: 'Profile lengkap per pelanggan (Transit/Owner/Renter/Member), riwayat kapal dengan flag state & IMO number.',
      bullets: ['Customer portal access', 'Document tracking', 'Unified billing info'],
    },
    {
      category: 'INSIGHTS',
      icon: LuTrendingUp,
      title: 'Analytics & Reports',
      desc: 'Occupancy trend, revenue mix, top customers, frequent visiting boats, delayed arrivals. Export CSV/PDF.',
      bullets: ['Real-time charts', 'Per-period filters', 'Forecast AI (Phase 14+)'],
    },
  ]

  return (
    <Box className={classes.sectionAlt}>
      <Container size="xl">
        <Stack align="center" mb="xl" gap="xs">
          <Title order={2} ta="center" className={classes.sectionTitle}>
            Fitur Inti JP MARINE
          </Title>
          <Text className={classes.sectionSubtitle}>
            Semua yang Anda butuhkan untuk mengelola marina modern — dari reservasi hingga tagihan.
          </Text>
        </Stack>

        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
          {features.map((f) => (
            <Box key={f.title} className={classes.card}>
              <Group gap="sm" mb="xs">
                <ThemeIcon variant="light" color="laut" size="lg" radius="md">
                  <f.icon size={20} />
                </ThemeIcon>
                <Text className={classes.cardCategory}>{f.category}</Text>
              </Group>
              <Text className={classes.cardTitle}>{f.title}</Text>
              <Text className={classes.cardDesc}>{f.desc}</Text>
              <List
                size="sm"
                spacing={4}
                icon={
                  <ThemeIcon color="laut" variant="light" size={18} radius="xl">
                    <LuCheck size={11} />
                  </ThemeIcon>
                }
              >
                {f.bullets.map((b) => (
                  <List.Item key={b}>
                    <Text component="span" className={classes.cardBullet}>
                      {b}
                    </Text>
                  </List.Item>
                ))}
              </List>
            </Box>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  )
}

// ─── INDONESIA ADVANTAGE ─────────────────────────────

function IndonesiaAdvantage() {
  const items = [
    {
      emoji: '🇮🇩',
      title: 'Native Bahasa',
      desc: 'Default Bahasa Indonesia, UI ditulis oleh orang Indonesia. English one-click toggle.',
    },
    { emoji: '💳', title: 'QRIS & Bank Lokal', desc: 'Midtrans, Xendit, QRIS, transfer BCA/Mandiri/BRI siap pakai.' },
    { emoji: '📊', title: 'PPN 11% Built-in', desc: 'Tax rate configurable, invoice format sesuai DJP.' },
    {
      emoji: '⏰',
      title: 'WIB/WITA/WIT',
      desc: 'Default Asia/Makassar (Bali). Multi-TZ kalau punya marina lintas pulau.',
    },
    { emoji: '💬', title: 'Support WhatsApp', desc: 'Bukan cuma email. Tim support berbasis di Bali.' },
    { emoji: '🔒', title: 'UU PDP Compliant', desc: 'Hosting Jakarta, enkripsi AES-256, DPA tersedia.' },
  ]
  return (
    <Container size="xl" py={80}>
      <Stack align="center" mb="xl" gap="xs">
        <Title order={2} ta="center" className={classes.sectionTitle}>
          Dibangun untuk Marina Indonesia
        </Title>
        <Text className={classes.sectionSubtitle}>Bukan software Barat yang di-translate. Kami tahu bedanya.</Text>
      </Stack>
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
        {items.map((i) => (
          <Box key={i.title} className={classes.card}>
            <Text className={classes.cardEmoji}>{i.emoji}</Text>
            <Text className={classes.cardTitle}>{i.title}</Text>
            <Text className={classes.cardDesc}>{i.desc}</Text>
          </Box>
        ))}
      </SimpleGrid>
    </Container>
  )
}

// ─── PRICING ─────────────────────────────────────────

type Tier = {
  name: string
  price: string
  period: string
  hint: string
  features: string[]
  cta: string
  popular: boolean
  /** When true, the CTA button is the demo flow (1-click into dashboard).
      When false, it's a /contact link (sales-led). Starter = demo because
      that's the "self-serve" path; Professional/Enterprise = sales because
      pricing requires a conversation. */
  ctaIsDemo: boolean
}

function Pricing() {
  const tiers: Tier[] = [
    {
      name: 'Starter',
      price: 'Rp 1.500.000',
      period: '/bulan',
      hint: 'Cocok untuk marina baru',
      features: ['Hingga 20 berth', 'Hingga 500 customer', 'Semua fitur core', 'Email support'],
      cta: 'Coba Demo Live',
      popular: false,
      ctaIsDemo: true,
    },
    {
      name: 'Professional',
      price: 'Rp 3.500.000',
      period: '/bulan',
      hint: 'Untuk marina berkembang',
      features: [
        'Unlimited berth & customer',
        'Analytics + reports',
        'Long-term contracts',
        'Live chat customer portal',
        'WhatsApp support prioritas',
        'Custom branding',
      ],
      cta: 'Pilih Professional',
      popular: true,
      ctaIsDemo: false,
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      hint: 'Untuk grup marina',
      features: [
        'Multi-marina',
        'Dedicated account manager',
        'SLA 99.9%',
        'Custom integration',
        'White-label opsional',
        'Training on-site',
      ],
      cta: 'Hubungi Sales',
      popular: false,
      ctaIsDemo: false,
    },
  ]

  return (
    <Box className={classes.sectionAlt}>
      <Container size="xl">
        <Stack align="center" mb="xl" gap="xs">
          <Title order={2} ta="center" className={classes.sectionTitle}>
            Harga Transparan
          </Title>
          <Text className={classes.sectionSubtitle}>Tanpa hidden fee. Bayar bulanan, cancel kapan saja.</Text>
        </Stack>
        <SimpleGrid cols={{ base: 1, md: 3 }}>
          {tiers.map((t) => (
            <Box key={t.name} className={`${classes.card} ${t.popular ? classes.pricingPopular : ''}`}>
              {t.popular && (
                <Badge color="laut" variant="filled" mb="md">
                  Paling Populer
                </Badge>
              )}
              <Text fw={700} fz="xl" mb={2}>
                {t.name}
              </Text>
              <Text className={classes.cardDesc} mb="md">
                {t.hint}
              </Text>
              <Group align="baseline" gap={4} mb="md">
                <Text className={classes.priceAmount}>{t.price}</Text>
                <Text className={classes.pricePeriod}>{t.period}</Text>
              </Group>
              <Divider mb="md" />
              <List
                size="sm"
                spacing={6}
                icon={
                  <ThemeIcon color="laut" variant="light" size={18} radius="xl">
                    <LuCheck size={11} />
                  </ThemeIcon>
                }
                mb="lg"
              >
                {t.features.map((f) => (
                  <List.Item key={f}>
                    <Text component="span" className={classes.cardBullet}>
                      {f}
                    </Text>
                  </List.Item>
                ))}
              </List>
              {t.ctaIsDemo ? (
                <DemoCTA size="md" variant="soft" fullWidth />
              ) : (
                <Button component={Link} to="/contact" color="laut" variant={t.popular ? 'filled' : 'light'} fullWidth>
                  {t.cta}
                </Button>
              )}
            </Box>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  )
}

// ─── FAQ ─────────────────────────────────────────────

function Faq() {
  const qas = [
    {
      q: 'Berapa lama proses onboarding?',
      a: 'Setup akun + import data dari spreadsheet biasanya selesai dalam 1 jam. Tim kami bantu migrasi gratis untuk subscriber Professional+.',
    },
    {
      q: 'Apakah data kami aman?',
      a: 'Ya. Data disimpan di server Indonesia (Jakarta, CBN Biznet), dienkripsi AES-256, backup harian otomatis. Kepatuhan UU PDP.',
    },
    {
      q: 'Bisa import data dari Excel?',
      a: 'Bisa. Kami support import CSV/XLSX untuk customer, boat, booking histori. Tim onboarding bantu mapping kolom.',
    },
    {
      q: 'Ada limit jumlah customer?',
      a: 'Starter: 500 customer, 20 berth. Professional: unlimited. Enterprise: custom + multi-marina.',
    },
    {
      q: 'Bagaimana integrasi dengan software accounting?',
      a: 'Support export ke QuickBooks, Accurate Indonesia, Jurnal.id via CSV. API terbuka untuk integrasi custom (Phase 14).',
    },
    {
      q: 'Apakah ada mobile app?',
      a: 'Web app responsive, bekerja di mobile browser. Native iOS/Android di roadmap Phase 14+.',
    },
  ]
  return (
    <Container size="md" py={80}>
      <Title order={2} ta="center" mb="xl" className={classes.sectionTitle}>
        Pertanyaan Sering Ditanya
      </Title>
      <Accordion variant="separated" radius="md">
        {qas.map((qa, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static FAQ list
          <Accordion.Item key={i} value={String(i)}>
            <Accordion.Control>{qa.q}</Accordion.Control>
            <Accordion.Panel>
              <Text className={classes.faqAnswer}>{qa.a}</Text>
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    </Container>
  )
}

// ─── FINAL CTA ───────────────────────────────────────

function FinalCta() {
  return (
    <Box className={classes.finalCta}>
      <Container size="md">
        <Stack align="center" gap="md">
          <Title order={2} c="white" ta="center">
            Siap Modernisasi Marina Anda?
          </Title>
          <Text c="white" ta="center" maw={500} opacity={0.9}>
            Bergabung dengan marina-marina progresif di Indonesia. Setup dalam 1 jam, trial 30 hari gratis.
          </Text>
          <Group gap="sm" mt="md">
            <DemoCTA size="lg" variant="inverted" />
            <Button
              component={Link}
              to="/contact"
              size="lg"
              variant="outline"
              c="white"
              style={{ borderColor: 'rgba(255,255,255,0.6)' }}
            >
              Hubungi Sales
            </Button>
          </Group>
        </Stack>
      </Container>
    </Box>
  )
}

// ─── CLIENT STRIP ────────────────────────────────────

function ClientStrip() {
  const clients = [
    'Benoa Yachting Club',
    'Marina Nusa Dua',
    'Batam Marina Resort',
    'Labuan Bajo Club',
    'Pelabuhan Marina Lombok',
    'Gili Trawangan Marina',
  ]
  return (
    <Container size="xl" py="lg">
      <Text
        ta="center"
        size="xs"
        c="dimmed"
        fw={600}
        mb="md"
        style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}
      >
        Dipercaya oleh marina di seluruh Indonesia
      </Text>
      <Group justify="center" gap="xl" wrap="wrap">
        {clients.map((c) => (
          <Text key={c} className={classes.clientName}>
            {c}
          </Text>
        ))}
      </Group>
    </Container>
  )
}

// ─── HOW IT WORKS ─────────────────────────────────────

function HowItWorks() {
  const steps = [
    {
      num: '01',
      title: 'Daftar & Konfigurasi',
      desc: 'Setup pontoon, berth, dan undang tim Anda. Import data dari spreadsheet lama. Selesai dalam 1 jam — bukan 1 minggu.',
    },
    {
      num: '02',
      title: 'Terima Booking',
      desc: 'Walk-in, telepon, email, radio, agen — semua masuk satu dashboard. Approve, assign berth, kirim konfirmasi otomatis.',
    },
    {
      num: '03',
      title: 'Kelola & Tagih',
      desc: 'Check-in/out digital. Invoice PPN 11% otomatis. Laporan occupancy dan revenue real-time. Kontrak jangka panjang.',
    },
  ]
  return (
    <Container size="xl" py={80}>
      <Stack align="center" mb="xl" gap="xs">
        <Title order={2} ta="center" className={classes.sectionTitle}>
          Mulai Dalam 3 Langkah
        </Title>
        <Text className={classes.sectionSubtitle}>
          Dari spreadsheet ke manajemen marina modern — dalam hitungan jam, bukan bulan.
        </Text>
      </Stack>
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xl">
        {steps.map((s, i) => (
          <Stack key={s.num} gap="md" align="center" ta="center" style={{ position: 'relative' }}>
            <Box className={classes.stepNum}>{s.num}</Box>
            <Text fw={700} fz="lg" style={{ letterSpacing: '-0.01em' }}>
              {s.title}
            </Text>
            <Text className={classes.cardDesc} ta="center">
              {s.desc}
            </Text>
            {i < steps.length - 1 && (
              <Box className={classes.stepConnector} visibleFrom="sm">
                →
              </Box>
            )}
          </Stack>
        ))}
      </SimpleGrid>
      <Stack align="center" mt="xl">
        <DemoCTA size="md" />
        <Text size="xs" c="dimmed">
          Tidak perlu kartu kredit · Akses penuh 30 hari
        </Text>
      </Stack>
    </Container>
  )
}

// ─── TESTIMONIALS ─────────────────────────────────────

function Testimonials() {
  const testimonials = [
    {
      quote:
        'Setup dalam 1 jam, tim langsung produktif. Tidak ada lagi booking double-book karena semua real-time di satu layar.',
      name: 'Kadek Wirawan',
      role: 'Manajer Operasional',
      marina: 'Nusa Dua Yachting Club, Bali',
    },
    {
      quote:
        'Laporan keuangan yang dulu makan waktu sehari, sekarang tinggal klik. QRIS-nya seamless — pelanggan asing pun bisa bayar.',
      name: 'Ahmad Fauzi',
      role: 'Direktur',
      marina: 'Marina Batam Raya, Kepri',
    },
    {
      quote:
        'Pelanggan internasional suka terima invoice digital. Pendapatan naik karena tidak ada lagi booking yang lewat begitu saja.',
      name: 'Made Suardana',
      role: 'Kepala Marina',
      marina: 'Labuan Bajo Marine Club, NTT',
    },
  ]

  return (
    <Container size="xl" py={80}>
      <Stack align="center" mb="xl" gap="xs">
        <Title order={2} ta="center" className={classes.sectionTitle}>
          Kata Marina yang Sudah Bergabung
        </Title>
        <Text className={classes.sectionSubtitle}>Lebih dari 50 marina di Indonesia sudah beralih ke JP MARINE.</Text>
      </Stack>
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        {testimonials.map((t) => (
          <Box key={t.name} className={classes.testimonialCard}>
            <Text className={classes.testimonialQuote} mb="md">
              "{t.quote}"
            </Text>
            <Group gap="sm">
              <Box className={classes.testimonialAvatar}>{t.name.charAt(0)}</Box>
              <div>
                <Text size="sm" fw={600}>
                  {t.name}
                </Text>
                <Text size="xs" c="dimmed">
                  {t.role}
                </Text>
                <Text size="xs" c="dimmed">
                  {t.marina}
                </Text>
              </div>
            </Group>
          </Box>
        ))}
      </SimpleGrid>
    </Container>
  )
}

// ─── FOOTER ──────────────────────────────────────────

function Footer() {
  return (
    <Box className={classes.footer}>
      <Container size="xl">
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="xl" mb="xl">
          <Stack gap="xs">
            <Group gap="xs" mb="xs">
              <BrandIcon />
              <Text fw={700} c="white">
                JP MARINE
              </Text>
            </Group>
            <Text className={classes.footerText}>Marina management software untuk Indonesia.</Text>
            <Text className={classes.footerText}>📍 Jl. Marina No. 1, Denpasar Bali</Text>
            <Text className={classes.footerText}>✉️ support@open-marina.id</Text>
          </Stack>
          <Stack gap={6}>
            <Text className={classes.footerHeading}>Produk</Text>
            <FooterLink to="/features">Fitur</FooterLink>
            <FooterLink to="/pricing">Harga</FooterLink>
            <FooterLink to="/contact">Kontak</FooterLink>
            <FooterLink to="/login">Login Staff</FooterLink>
          </Stack>
          <Stack gap={6}>
            <Text className={classes.footerHeading}>Perusahaan</Text>
            <FooterLink to="/contact">Tentang Kami</FooterLink>
            <FooterLink to="/contact">Karir</FooterLink>
            <FooterLink to="/contact">Press Kit</FooterLink>
          </Stack>
          <Stack gap={6}>
            <Text className={classes.footerHeading}>Legal</Text>
            <FooterLink to="/legal/terms">Syarat & Ketentuan</FooterLink>
            <FooterLink to="/legal/privacy">Kebijakan Privasi</FooterLink>
          </Stack>
        </SimpleGrid>
        <Box className={classes.footerDivider} />
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Text className={classes.footerCopy}>© 2026 JP MARINE · Hak cipta dilindungi undang-undang</Text>
          <Group gap="sm">
            <ActionIcon variant="subtle" color="gray" component="a" href="#" aria-label="Bahasa">
              <LuLanguages size={16} />
            </ActionIcon>
            <Text className={classes.footerCopy}>Bahasa Indonesia</Text>
          </Group>
        </Group>
      </Container>
    </Box>
  )
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Anchor component={Link} to={to as any} className={classes.footerLink}>
      {children}
    </Anchor>
  )
}

// ─── DEMO CTA ─────────────────────────────────────────
// Single source-of-truth for the "try the product" call-to-action. When
// ENABLE_DEMO=true the click mints a session and lands the user in
// /dashboard as MANAGER (zero forms). When disabled, the same button
// falls back to /contact for sales-led onboarding. Use this everywhere
// the page wants to ask the visitor to try JP MARINE.

type DemoCtaVariant = 'primary' | 'soft' | 'inverted'

function DemoCTA({
  size = 'lg',
  variant = 'primary',
  fullWidth = false,
}: {
  size?: 'sm' | 'md' | 'lg'
  variant?: DemoCtaVariant
  fullWidth?: boolean
}) {
  const [pending, setPending] = useState(false)
  const config = useQuery({
    queryKey: ['demo', 'config'],
    queryFn: async () => {
      const res = await fetch('/api/demo/config')
      if (!res.ok) return { enabled: false }
      return (await res.json()) as { enabled: boolean }
    },
    retry: false,
    staleTime: 5 * 60_000,
  })

  // Style mapping per variant — keep call sites declarative.
  const styles =
    variant === 'inverted'
      ? { color: undefined, bgVariant: 'white' as const, c: 'teal.7' }
      : variant === 'soft'
        ? { color: 'teal' as const, bgVariant: 'light' as const }
        : { color: 'orange' as const, bgVariant: 'filled' as const }

  // While loading config (or disabled), render the fallback to avoid layout
  // shift — same size + label format, just routes to /contact instead.
  if (!config.data?.enabled) {
    return (
      <Button
        component={Link}
        to="/contact"
        size={size}
        color={styles.color}
        variant={styles.bgVariant}
        c={styles.c}
        fullWidth={fullWidth}
        rightSection={<LuArrowRight size={size === 'sm' ? 14 : 18} />}
      >
        Coba Gratis 30 Hari
      </Button>
    )
  }

  async function start() {
    setPending(true)
    try {
      const res = await fetch('/api/demo/start', { method: 'POST', credentials: 'include' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const body = (await res.json()) as { redirectTo?: string }
      // Hard navigate so the new session cookie is fresh on dashboard's first
      // render (avoids a "/login flash → /dashboard" double-route).
      window.location.assign(body.redirectTo ?? '/dashboard')
    } catch (e) {
      setPending(false)
      console.error('[Demo] start failed', e)
      alert('Demo tidak tersedia saat ini. Silakan coba lagi.')
    }
  }

  return (
    <Button
      onClick={start}
      loading={pending}
      size={size}
      color={styles.color}
      variant={styles.bgVariant}
      c={styles.c}
      fullWidth={fullWidth}
      leftSection={<LuFlaskConical size={size === 'sm' ? 14 : 18} />}
      rightSection={!pending ? <LuArrowRight size={size === 'sm' ? 14 : 18} /> : undefined}
    >
      Coba Demo Live
    </Button>
  )
}
